<template>
  <div v-if="(showDefinedOnly && internalValue !== null) || (!showDefinedOnly  && (showAdvanced || !property.advanced))">
    <v-layout align-center>
      <v-flex
          shrink
          pl-1 pr-1
          :style="displayIfDefined"
          @mouseenter="disabled=false"
          @mouseleave="disabled=true"
      >
        <v-tooltip right>
          <template v-slot:activator="{ on }">
            <v-icon
                v-on="on"
                color="primary"
                :disabled="disabled"
                @click="value=null"
            >
              mdi-window-close
            </v-icon>
          </template>
          <span>{{ $t('parameters.label.backToDefaultValue') }}</span>
        </v-tooltip>
      </v-flex>
      <v-flex shrink pl-1 pr-1>
        <v-tooltip v-if="compact && property.description" top>
          <template v-slot:activator="{ on }">
            <div v-on="on" style="width: 270px" class="subheading pl-3 pr-3">{{ shortname }}</div>
          </template>
          <span>{{ property.description }}</span>
        </v-tooltip>
        <div v-else style="width: 270px" class="subheading pl-3 pr-3">{{ shortname }}</div>
      </v-flex>
      <v-flex grow pl-1 pr-1>
        <ObjectMapParameter
            v-if="objectMap"
            v-bind:property="property"
            :languageRestrictions="languageRestrictions"
        ></ObjectMapParameter>
        <v-select
            v-else-if="property.format && property.format === 'label'"
            :label="$t('parameters.label.labelName')"
            :items="extendedLabelsListForSelect"
            item-text="label"
            item-value="name"
            :multiple="property.type === 'array'"
            :chips="property.type === 'array'"
            v-model="value"
            single-line
        ></v-select>
        <v-select
            v-else-if="property.format && property.format === 'labelset'"
            :label="$t('parameters.label.labelSet')"
            :items="labelSets"
            item-text="label"
            item-value="name"
            :multiple="property.type === 'array'"
            :chips="property.type === 'array'"
            v-model="value"
            single-line
        ></v-select>
        <v-select
            v-else-if="property.format && property.format === 'lexicon'"
            :label="$t('parameters.label.lexicon')"
            :items="lexicons"
            item-text="label"
            item-value="name"
            :multiple="property.type === 'array'"
            :chips="property.type === 'array'"
            v-model="value"
            single-line
        ></v-select>
        <v-select
            v-else-if="property.format && property.format === 'vectorizer'"
            :label="$t('parameters.label.vectorizer')"
            :items="vectorizers"
            item-text="label"
            item-value="name"
            :multiple="property.type === 'array'"
            :chips="property.type === 'array'"
            v-model="value"
            single-line
        ></v-select>
        <v-select
            v-else-if="property.format && property.format === 'search-params-builder'"
            :label="$t('parameters.label.searchParamsBuilder')"
            :items="searchParamsBuilders"
            item-text="label"
            item-value="name"
            :multiple="property.type === 'array'"
            :chips="property.type === 'array'"
            v-model="value"
            single-line
        ></v-select>
        <v-autocomplete
            v-else-if="property.enum"
            :label="property.default"
            :items="property.enum"
            v-model="value"
            single-line
            style="width: 300px"
            clearable
            @click:clear="clear"
        ></v-autocomplete>
        <v-checkbox
            v-else-if="property.type === 'boolean'"
            :rules="rules"
            v-model="value"
            style="width: 250px"
        ></v-checkbox>
        <v-select
            v-else-if="isLanguageFormat"
            :label="$t('parameters.label.language')"
            :items="possibleLanguages"
            v-model="value"
            single-line
            style="width: 300px"
        ></v-select>
        <v-select
            v-else-if="isLexiconFormat"
            v-model="value"
            :items="lexicons"
            item-text="label"
            item-value="name"
            :label="$t('parameters.label.lexicon')"
        ></v-select>
        <v-text-field
            v-else-if="isJsonFormat"
            v-model="value"
            px-5 mx-5
        >
          <template #append-outer>
            <v-tooltip bottom open-delay="100">
              <template v-slot:activator="{ on }">
                <v-btn
                    v-on="on"
                    icon
                    small
                    color="primary"
                    @click="jsonEditorDialog = true"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('app.button.edit') }}</span>
            </v-tooltip>
          </template>
        </v-text-field>
        <v-text-field
            v-else-if="isMultilineTextFormat"
            readonly
            disabled
            class="removeBorder">
          <template #append-outer>
            <v-tooltip bottom open-delay="100">
              <template v-slot:activator="{ on }">
                <v-btn
                    v-on="on"
                    icon
                    small
                    color="primary"
                    @click="multilineEditorDialog = true"
                >
                  <v-icon>mdi-arrow-expand</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('app.button.expand') }}</span>
            </v-tooltip>
          </template>
        </v-text-field>
        <!-- FIXME debounce model updates (looks like vue has a debounce feature? https://github.com/vuejs/vue/issues/1025) -->
        <v-text-field
            v-else
            :rules="rules"
            :placeholder="property.default"
            v-model="value"
            style="width: 250px"
        ></v-text-field>
      </v-flex>
    </v-layout>
    <v-layout v-if="isMultilineTextFormat" class="px-5 mx-5">
      <v-textarea
          v-model="value"
          auto-grow
          outlined
      ></v-textarea>
    </v-layout>
    <v-layout v-if="!compact">
      <v-flex grow pl-1 pr-1>
        <div class="pl-4 ml-4 pr-4 mb-3 font-weight-light body-2 font-italic" v-html="description"></div>
        <v-divider></v-divider>
      </v-flex>
    </v-layout>
    <v-dialog
        v-model="jsonEditorDialog"
        max-width="640"
        @click:outside="jsonEditorDialog = false"
        @keydown.esc="jsonEditorDialog = false"
    >
      <v-card height="460px">
        <v-card-title class="headline">{{ $t("parameters.label.jsonEditor") }}</v-card-title>
        <v-card-text>
          <v-jsoneditor
              v-model="jsonValueModel"
              :options="options"
              :plus="false"
              height="320px"
          ></v-jsoneditor>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="jsonEditorDialog = false">
            {{ $t("app.button.apply") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
        v-model="multilineEditorDialog"
        max-width="640"
        @click:outside="multilineEditorDialog = false"
        @keydown.esc="multilineEditorDialog = false"
    >
      <v-card height="fit-content" class="pt-2">
        <v-card-title>Prompt</v-card-title>
        <v-card-text height="fit-content" class="pb-0">
          <v-textarea
              v-model="value"
              :value="value"
              outlined
              rows="12"
              height="fit-content"
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="mx-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="multilineEditorDialog = false">
            {{ $t("app.button.apply") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ObjectMapParameter from "./ObjectMapParameter";
import {mapGetters, mapState} from "vuex";
import {markdownToHTML} from "@/utils/markdown";

export default {
  inject: ['setParamValue', 'removeParam'],
  components: {
    ObjectMapParameter
  },
  props: ["property", "showAdvanced", "showDefinedOnly", "compact", "languageRestrictions", "vectorizers", "searchParamsBuilders"],
  data() {
    return {
      options: {
        mode: "code",
        mainMenuBar: true,
        statusBar: false,
      },
      disabled: true,
      mandatoryParameter: v => !!v,
      nonNegativeInteger: v => (v === null || !isNaN(v) && v >= 0) || 'Non-negative integer required',
      negativeInteger: v => (v === null || !isNaN(v) && v < 0) || 'Negative integer required',
      nonPositiveInteger: v => (v === null || !isNaN(v) && v <= 0) || 'Non-positive integer required',
      positiveInteger: v => (v === null || !isNaN(v) && v > 0) || 'Positive integer required',
      internalValue: null,
      jsonEditorDialog: false,
      multilineEditorDialog: false,
    };
  },
  mounted() {
    if (typeof this.property.value !== "undefined") {
      this.internalValue = this.property.value;
      this.setParamValue(this.property.name, this.property.value);
    }
  },
  computed: {
    ...mapState("lexicons", ["lexicons"]),
    ...mapState("settings", ["labelSets"]),
    ...mapGetters("settings", ["extendedLabelsListForSelect"]),
    ...mapGetters(["languages"]),
    possibleLanguages() {
      if (this.property.format !== "language")
        return [];
      if (this.languageRestrictions)
        return this.languages.filter(it => this.languageRestrictions.includes(it.value))
      else
        return this.languages
    },
    objectMap() {
      return this.property.type === "object"
    },
    isLexiconFormat() {
      return this.property.format === "lexicon" // && this.property.type === "object" TODO why test object ?
    },
    isJsonFormat() {
      return this.property.format === "json";
    },
    isMultilineTextFormat() {
      return this.property.format === "multiline";
    },
    isLanguageFormat() {
      return this.property.format === "language";
    },
    jsonValueModel: {
      get() {
        if (!this.value || !this.valueIsJson) return null;
        return JSON.parse(this.value);
      },
      set(newJsonValue) {
        this.value = JSON.stringify(newJsonValue);
      },
    },
    value: {
      get() {
        if (this.property.type === 'boolean') {
          if (this.internalValue === null) {
            return this.property.default
          } else {
            return this.internalValue
          }
        } else {
          return this.internalValue
        }
        // return this.internalValue
      },
      set(value) {
        this.internalValue = value;
        let typedValue = value;
        if (this.property.type === "integer" || this.property.type === "number") {
          if (value !== null && value !== "") {
            typedValue = Number(value)
            this.setParamValue(this.property.name, typedValue);
          } else {
            this.removeParam(this.property.name);
          }
        } else if (this.property.type === "boolean") {
          if (value !== null) {
            typedValue = Boolean(value)
            this.setParamValue(this.property.name, typedValue);
          } else {
            this.removeParam(this.property.name);
          }
        } else {
          if (value !== "" && value !== null) {
            this.setParamValue(this.property.name, typedValue);
          } else {
            this.removeParam(this.property.name);
          }
        }
      }
    },
    displayIfDefined() {
      if (this.internalValue !== null) {
        return ""
      } else {
        return "visibility: hidden;"
      }
    },
    shortname() {
      //const lastIndex = this.property.name.lastIndexOf('.')
      //return lastIndex === -1 ? this.property.name : this.property.name.substr(lastIndex+1, this.property.name.length)
      return this.property.title
    },
    description() {
      return markdownToHTML(this.property.description)
    },
    rules() {
      const result = []
      // if (!this.property.default) {
      //   result.push(this.mandatoryParameter)
      // }
      if (this.property.minimum === 0 && (!this.property.maximum && !this.property.exclusiveMaximum)) {
        result.push(this.nonNegativeInteger)
      } else if (this.property.exclusiveMinimum === 0 && (!this.property.maximum && !this.property.exclusiveMaximum)) {
        result.push(this.positiveInteger)
      } else if (this.property.maximum === 0 && (!this.property.minimum && !this.property.exclusiveMinimum)) {
        result.push(this.nonPositiveInteger)
      } else if (this.property.exclusiveMaximum === 0 && (!this.property.minimum && !this.property.exclusiveMinimum)) {
        result.push(this.negativeInteger)
      // } else {
      //   console.log("unhandled rule situation for property:");
      //   console.log(JSON.stringify(this.property))
      }
      return result;
    },
  },
  methods: {
    clear() {
      this.$emit("clear")
    },
  },
};
</script>

<style>
.removeBorder > .v-input__control > .v-input__slot::before {
  border-style: none !important;
}
</style>